import React from "react";
import { MessageCircle, Calendar, Loader2 } from "lucide-react";
import { whatsappLink } from "../../lib/whatsapp";

const CHIP_CLASS = "text-[11px] font-medium px-3 py-1.5 rounded-full glass border border-white/10 text-white/85 hover:border-brand-orange hover:text-brand-orange transition-all";

const ChipRow = ({ items, makeId, testId, onChip }) => (
  <div className="flex flex-wrap gap-1.5 pt-2" data-testid={testId}>
    {items.map((item) => (
      <button
        key={item}
        onClick={() => onChip(item)}
        data-testid={makeId(item)}
        className={CHIP_CLASS}
      >
        {item}
      </button>
    ))}
  </div>
);

const EndCta = ({ q, qual, locale }) => (
  <div className="flex flex-col gap-2 pt-2" data-testid="chatbot-cta">
    <a
      href="/book"
      target="_self"
      data-testid="chatbot-cta-book"
      className="inline-flex items-center justify-center gap-2 text-xs font-semibold px-4 py-2.5 rounded-full bg-brand-orange text-white hover:bg-brand-orange/90 transition-all"
    >
      <Calendar className="w-3.5 h-3.5" /> {q.book}
    </a>
    <a
      href={whatsappLink(locale === "de"
        ? `Hallo, ich habe gerade euer Angebots-Formular im Chat ausgefüllt: ${qual.name} — ${qual.service} — ${qual.budget} — ${qual.timeline}.`
        : `Hi, I just filled out your chat qualification form: ${qual.name} — ${qual.service} — ${qual.budget} — ${qual.timeline}.`)}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="chatbot-cta-whatsapp"
      className="inline-flex items-center justify-center gap-2 text-xs font-semibold px-4 py-2.5 rounded-full glass border border-[#25D366]/40 text-white/90 hover:border-[#25D366] transition-all"
    >
      <MessageCircle className="w-3.5 h-3.5 text-[#25D366]" /> {q.whatsapp}
    </a>
    <a
      href="/contact"
      data-testid="chatbot-cta-contact"
      className="inline-flex items-center justify-center gap-2 text-xs font-medium px-4 py-2.5 rounded-full glass border border-white/10 text-white/80 hover:border-white/30 transition-all"
    >
      {q.contact}
    </a>
  </div>
);

export const ChatMessages = ({ bot, t, locale, scrollRef }) => (
  <div ref={scrollRef} data-testid="chatbot-messages" className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
    {bot.messages.map((m, i) => (
      <div key={`${i}-${m.role}`} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
        <div
          className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed whitespace-pre-line ${
            m.role === "user"
              ? "bg-brand-azure text-white rounded-br-sm"
              : "glass text-white/85 rounded-bl-sm"
          }`}
        >
          {m.content}
        </div>
      </div>
    ))}
    {bot.sending && (
      <div className="flex justify-start">
        <div className="glass rounded-2xl rounded-bl-sm px-4 py-2.5 text-sm text-white/60 flex items-center gap-2">
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
          <span className="font-mono text-xs">thinking…</span>
        </div>
      </div>
    )}

    {bot.showGreetingChips && (
      <div className="flex flex-wrap gap-2 pt-2" data-testid="chatbot-mode-chips">
        <button
          onClick={bot.startQualification}
          data-testid="chatbot-chip-qualification"
          className="text-xs font-medium px-3.5 py-2 rounded-full bg-brand-orange text-white hover:bg-brand-orange/90 transition-all hover:-translate-y-0.5 shadow-lg shadow-brand-orange/25"
        >
          {t.chatbot.chip_qual}
        </button>
        <button
          onClick={bot.startAIChat}
          data-testid="chatbot-chip-ai"
          className="text-xs font-medium px-3.5 py-2 rounded-full glass border border-white/15 text-white/85 hover:border-brand-azure/60 transition-all"
        >
          {t.chatbot.chip_ai}
        </button>
      </div>
    )}

    {bot.showServiceChips && (
      <ChipRow items={bot.q.services} makeId={(s) => `chatbot-service-${s.replace(/\s+/g, "-").toLowerCase()}`} testId="chatbot-service-chips" onChip={bot.handleChip} />
    )}
    {bot.showBudgetChips && (
      <ChipRow items={bot.q.budgets} makeId={(b) => `chatbot-budget-${b.replace(/\s+/g, "-")}`} testId="chatbot-budget-chips" onChip={bot.handleChip} />
    )}
    {bot.showTimelineChips && (
      <ChipRow items={bot.q.timelines} makeId={(tl) => `chatbot-timeline-${tl.replace(/\s+/g, "-")}`} testId="chatbot-timeline-chips" onChip={bot.handleChip} />
    )}

    {bot.showSkipChip && (
      <div className="flex pt-2" data-testid="chatbot-skip-chip">
        <button
          onClick={bot.handleSkip}
          data-testid="chatbot-skip"
          className="text-[11px] font-medium px-3.5 py-1.5 rounded-full glass border border-white/15 text-white/70 hover:border-brand-orange hover:text-brand-orange transition-all"
        >
          {bot.q.skip} →
        </button>
      </div>
    )}

    {bot.showEndCta && <EndCta q={bot.q} qual={bot.qual} locale={locale} />}
  </div>
);
